import { useCallback, useRef, useState } from 'react';
import { useMicCapture, UseMicCaptureResult } from './useMicCapture';
import { useAzureSpeech } from './useAzureSpeech';

// Orchestrates the two mic transcription backends behind one interface:
//   1. Azure AI Speech (high accuracy, low-latency interim results) when the
//      environment has a Speech resource configured.
//   2. The browser Web Speech API as a graceful fallback when Speech is not
//      configured (token endpoint 503) or the SDK path errors.
//
// start() engages Azure first. If Azure reports the feature is off (503 via
// onUnavailable), it transparently downgrades to Web Speech with a single token
// mint. The active backend's state is projected through one interface.
export const useMicTranscription = (
  meetingId: string | undefined,
  onFinalResult: (text: string, speaker?: string) => void
): UseMicCaptureResult => {
  const [backend, setBackend] = useState<'azure' | 'web' | null>(null);
  const backendRef = useRef<'azure' | 'web' | null>(null);
  backendRef.current = backend;

  const web = useMicCapture(onFinalResult);
  const webRef = useRef(web);
  webRef.current = web;

  // Azure hands off to Web Speech when the feature is unconfigured (503).
  const handleAzureUnavailable = useCallback(() => {
    if (webRef.current.supported) {
      setBackend('web');
      webRef.current.start();
    } else {
      // No fallback available; leave backend on 'azure' so its (empty) state
      // shows and the mic simply doesn't engage.
      setBackend('azure');
    }
  }, []);

  const azure = useAzureSpeech(meetingId, onFinalResult, handleAzureUnavailable);

  const start = useCallback(() => {
    // Prefer Azure when we have a meeting to mint a token against.
    if (meetingId && azure.supported) {
      setBackend('azure');
      azure.start();
      return;
    }
    if (web.supported) {
      setBackend('web');
      web.start();
    }
  }, [meetingId, azure, web]);

  const stop = useCallback(() => {
    if (backendRef.current === 'azure') azure.stop();
    else if (backendRef.current === 'web') web.stop();
    else {
      azure.stop();
      web.stop();
    }
  }, [azure, web]);

  const active = backend === 'azure' ? azure : backend === 'web' ? web : null;

  return {
    supported: azure.supported || web.supported,
    listening: active ? active.listening : false,
    error: active ? active.error : null,
    interim: active ? active.interim : '',
    start,
    stop,
  };
};
